/* DoctorSelfPlanView — read-only Self-Plan case detail surface.
 *
 * Replaces the iter97.37 4-tab editor (`DoctorSelfPlanEditor`) which had
 * gotten in the way of the actual self-planning workflow: the wizard at
 * /doctor/cases/new already captures everything the doctor needs to author
 * (records + tooth movements + planning-software export + lab instructions),
 * so there was no value in re-asking for it in a separate 4-tab editor.
 *
 * This view shows:
 *   - A clean status card explaining where the case is.
 *   - PlanPanel rendering whatever the doctor authored (read-only).
 *   - Action buttons when status === "plan_authoring":
 *       • Edit Plan          → re-open the wizard at /doctor/cases/new?draft={id}&step=4
 *       • Send Plan to Lab   → PUT /api/cases/{id}/submit-to-production (single click)
 *   - Post-submit confirmation card when status moves past plan_authoring.
 *
 * No new fields, no auto-save, no tab switching. Author lives in the
 * wizard — this is just "what happens after submit" + how to finalise.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    ClipboardText, PaperPlaneTilt, PencilSimple, PaperPlaneRight,
} from "@phosphor-icons/react";
import PlanPanel from "@/components/case/PlanPanel";
import SectionCard from "@/components/case/SectionCard";
import { confirmDialog } from "@/lib/confirm";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso)
            .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
            .replace(/ /g, "-");
    } catch { return iso; }
};

const initials = (name) =>
    (name || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const STATUS_TO_LAB_LABEL = {
    submitted_to_production: "Sent · awaiting lab pickup",
    assigned_to_production: "In production at the lab",
    in_production: "In production at the lab",
    shipped: "Aligners shipped to your clinic",
    aligners_received: "Aligners received at your clinic",
    fitted: "Aligners delivered",
    treatment_started: "Patient is wearing aligners",
    in_treatment: "Patient is wearing aligners",
    completed: "Treatment complete",
};

export default function DoctorSelfPlanView({ c, onChange, canSubmit, doctorName }) {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const status = c.status;

    // Status taxonomy on this view:
    //   plan_authoring → the doctor finished the wizard but hasn't sent
    //                    the case to the lab yet (last chance to edit).
    //   Every status thereafter falls under `sentToLab` — the slim
    //   "Plan submitted to lab" header strip surfaces continuously
    //   through manufacturing → shipping → treatment so the doctor
    //   always knows the hand-off has happened, with the sub-line
    //   localising to the current stage (iter98.99df).
    const inAuthoring = status === "plan_authoring";
    const sentToLab = Object.prototype.hasOwnProperty.call(STATUS_TO_LAB_LABEL, status);

    const onEditPlan = () => {
        // Resume the wizard on the Treatment Plan step. Wizard supports
        // ?draft={id} to re-load the existing draft / plan_authoring case.
        navigate(`/doctor/cases/new?draft=${c.id}&step=4`);
    };

    const onSendToLab = async () => {
        if (!(await confirmDialog({ title: "Send this plan to the lab?", message: "You won't be able to edit it after submission.", confirmText: "Send to Lab", tone: "warn" }))) return;
        setSubmitting(true);
        try {
            await api.put(`/cases/${c.id}/submit-to-production`);
            toast.success("Plan submitted to lab — we'll take it from here");
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="self-plan-view">
            {/* Status card — three variants depending on where the case is. */}
            {inAuthoring && (
                <div
                    className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/80 via-white to-white p-5"
                    data-testid="self-plan-status-authoring"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-amber-800/80 flex items-center gap-2">
                                <PencilSimple size={11} weight="fill" />
                                Self-Plan · Awaiting your final send
                            </div>
                            <h3 className="font-display text-lg font-semibold tracking-tight mt-1.5">
                                Last chance to edit before the lab starts manufacturing
                            </h3>
                            <p className="text-[13px] text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                                Your plan is saved. You can refine any of the wizard sections
                                (clinical, tooth movements, attachments, lab instructions, files)
                                or send it straight to the lab.
                            </p>
                        </div>
                        {canSubmit && (
                            <div className="flex gap-2 flex-wrap shrink-0">
                                <Button
                                    onClick={onEditPlan}
                                    variant="outline"
                                    className="rounded-full h-9 px-4 text-xs font-semibold"
                                    data-testid="self-plan-edit-btn"
                                >
                                    <PencilSimple size={12} weight="fill" className="mr-1.5" />
                                    Edit Plan
                                </Button>
                                <Button
                                    onClick={onSendToLab}
                                    disabled={submitting}
                                    className="rounded-full h-9 px-4 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid={`submit-to-production-${c.id}`}
                                >
                                    <PaperPlaneRight size={12} weight="fill" className="mr-1.5" />
                                    {submitting ? "Sending…" : "Send Plan to Lab"}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {sentToLab && (
                <div
                    className="w-full rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50/70 via-card to-card px-5 sm:px-6 py-4 flex items-center justify-between gap-3"
                    data-testid="self-plan-status-sent"
                >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center font-semibold text-xs shrink-0">
                            {initials(doctorName) || (
                                <PaperPlaneTilt size={14} weight="fill" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-800/85">
                                Plan submitted to lab
                            </div>
                            <div className="text-sm font-semibold truncate text-foreground">
                                {doctorName || "You"}
                                <span className="text-muted-foreground font-normal"> · </span>
                                <span className="text-muted-foreground font-normal text-xs">
                                    {STATUS_TO_LAB_LABEL[status] || "Lab is on it"}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <div className="text-[11px] text-muted-foreground tabular-nums hidden md:block">
                            Sent {fmtDate(c.status_updated_at)}
                        </div>
                        <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-800"
                            data-testid="self-plan-status-pill"
                            title="Your plan has been handed off to manufacturing."
                        >
                            <PaperPlaneTilt size={10} weight="fill" /> Sent
                        </span>
                    </div>
                </div>
            )}

            {/* Read-only summary of whatever the doctor authored in the
                wizard. PlanPanel handles the empty state (e.g. when the
                doctor saved nothing into case.plan via the planner API). */}
            <PlanPanel
                caseId={c.id}
                plan={c.plan}
                status={c.status}
                caseFiles={c.files || []}
            />

            {/* Self-plan cases write most of their fields to `case.prescription`
                (set by the wizard), not `case.plan`. Show a small reminder so
                the doctor knows where to look for the authoritative data.
                iter98.99de — Wrapped in SectionCard so the rhythm matches
                the Submission tab brief. */}
            <SectionCard
                icon={ClipboardText}
                label="Where the full plan lives"
                testid="self-plan-hint-footer"
            >
                <div className="text-[12.5px] text-muted-foreground leading-relaxed">
                    Detailed tooth-movements, attachments, photos and your planning-software
                    export live on the <span className="font-semibold text-foreground">Submission</span> tab
                    above. Use <span className="font-semibold text-foreground">Edit Plan</span> to revise any field.
                </div>
            </SectionCard>
        </div>
    );
}
